import React from 'react';
import { motion } from 'framer-motion';

interface ScrollIndicatorProps {
  onClick?: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  color?: 'white' | 'black' | 'gray';
  delay?: number;
}

export function ScrollIndicator({
  onClick,
  className = '',
  size = 'md',
  color = 'white',
  delay = 0
}: ScrollIndicatorProps) {
  // 根据颜色设置样式
  const getColorClasses = () => {
    switch (color) {
      case 'white':
        return {
          border: 'border-white/60',
          bg: 'bg-white/80',
          hover: 'hover:border-white/80'
        };
      case 'black':
        return {
          border: 'border-black/60',
          bg: 'bg-black/80',
          hover: 'hover:border-black/80'
        };
      case 'gray':
        return {
          border: 'border-gray-400',
          bg: 'bg-gray-600',
          hover: 'hover:border-gray-500'
        };
      default:
        return {
          border: 'border-white/60',
          bg: 'bg-white/80',
          hover: 'hover:border-white/80'
        };
    }
  };

  // 根据尺寸设置样式
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          container: 'w-6 h-10',
          indicator: 'w-1 h-3 mt-2',
          padding: 'pt-2'
        };
      case 'lg':
        return {
          container: 'w-10 h-16',
          indicator: 'w-1.5 h-4 mt-3',
          padding: 'pt-3'
        };
      case 'md':
      default:
        return {
          container: 'w-8 h-14',
          indicator: 'w-1 h-4 mt-3',
          padding: 'pt-3'
        };
    }
  };

  const colorClasses = getColorClasses();
  const sizeClasses = getSizeClasses();

  return (
    <motion.div
      className={`cursor-pointer select-none ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, delay }}
      onClick={onClick}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      aria-label="滚动到下一节"
    >
      <motion.div
        className={`border-2 ${colorClasses.border} ${colorClasses.hover} rounded-full flex justify-center items-start ${sizeClasses.padding} transition-colors duration-300 ${sizeClasses.container}`}
      >
        <motion.div
          className={`rounded-full ${colorClasses.bg} ${sizeClasses.indicator}`}
          animate={{ y: [0, size === 'sm' ? 6 : size === 'lg' ? 10 : 8, 0] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>
    </motion.div>
  );
}

// 预设的常用变体
export function ScrollIndicatorWhite(props: Omit<ScrollIndicatorProps, 'color'>) {
  return <ScrollIndicator {...props} color="white" />;
}

export function ScrollIndicatorBlack(props: Omit<ScrollIndicatorProps, 'color'>) {
  return <ScrollIndicator {...props} color="black" />;
}

export function ScrollIndicatorGray(props: Omit<ScrollIndicatorProps, 'color'>) {
  return <ScrollIndicator {...props} color="gray" />;
}
